import { useCallback, useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useMap } from 'react-map-gl';
import type { MapMouseEvent } from 'mapbox-gl';
import {
  appStore,
  mapClickOwnersAtom,
  measureModeAtom,
  useMapClickOwner,
  useMapControl,
} from 'components/map';
import { isColorPickerOpen } from 'components/color-picker';
import {
  addCardHostAtom,
  coordinatesCursorAtom,
  coordinatesOpenAtom,
  removeCardHostAtom,
} from './atoms';
import { CoordinatesButton } from './coordinates-button';
import { CopyToast } from './copy-feedback';
import { type CopyResult, usePinActions } from './hooks';
import { MapContextMenu } from './map-context-menu';
import { type LngLat, wrapLngLat } from './coordinate-utils';

interface ContextMenuState {
  /** The right-clicked spot in the window, in pixels. */
  x: number;
  y: number;
  lngLat: LngLat;
}

/**
 * The event's position as `[lng, lat]`.
 *
 * @param {MapMouseEvent} e - A map mouse event.
 * @return {LngLat} Where the event happened.
 */
const eventLngLat = (e: MapMouseEvent): LngLat => wrapLngLat(e.lngLat);

const CLICK_OWNER = 'coordinate-pins';

/**
 * The Coordinates button in this map's control stack. The card is shared: it shows
 * beside one button at a time, and stays open while any map with a button remains —
 * a modal's map closing moves it to its page's. Once the last goes, it closes. Pins
 * stay.
 *
 * @return {null} Renders through the control stack.
 */
function CoordinatesControl(): null {
  const [host] = useState(() => Symbol('coordinates-control'));
  const addHost = useSetAtom(addCardHostAtom, appStore);
  const removeHost = useSetAtom(removeCardHostAtom, appStore);

  useMapControl({
    id: 'coordinate-pins',
    render: ({ className, closePanels }) => (
      <CoordinatesButton
        host={host}
        className={className}
        onOpen={closePanels}
      />
    ),
  });

  useEffect(() => {
    addHost(host);
    return () => removeHost(host);
  }, [host, addHost, removeHost]);

  return null;
}

/**
 * Dropping coordinate pins on one map, for moving a spot between Google Maps and the
 * map. Every map gets it through `CoordinatePinsOverlay`.
 *
 * - Right-click any spot for its coordinates, a pin, or Google Maps.
 * - The Coordinates button, whose card reads out the cursor's position, takes typed or
 *   pasted coordinates or a well or place, and lists the pins.
 * - While the card is open, clicks belong to it as they would to measuring: a click
 *   drops a pin and the map's popups stand down, unless measuring or another tool
 *   (`useMapClickOwner`) has the clicks.
 *
 * @return {JSX.Element} The button and the right-click menu.
 */
export function CoordinatePinsTool(): JSX.Element {
  const { current: mapRef } = useMap();
  const measureMode = useAtomValue(measureModeAtom);
  const owners = useAtomValue(mapClickOwnersAtom);
  const clicksTaken =
    measureMode || owners.some((owner) => owner !== CLICK_OWNER);
  const open = useAtomValue(coordinatesOpenAtom, appStore);
  const setCursor = useSetAtom(coordinatesCursorAtom, appStore);
  const { dropPin } = usePinActions();
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [copyToast, setCopyToast] = useState<CopyResult | null>(null);

  useMapClickOwner(CLICK_OWNER, open);

  // While the card is open the field follows the cursor and a click drops a pin.
  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map || !open) return undefined;
    const canvas = map.getCanvas();
    // A colour picker closes on mousedown, so the click that dismisses it still
    // reaches the map. Remember whether one was open when the press started and
    // treat that click as the dismissal it was.
    let pickerWasOpen = false;
    /**
     * Follows the cursor in the card's field.
     *
     * @param {MapMouseEvent} e - The move.
     */
    const onMove = (e: MapMouseEvent) => setCursor(eventLngLat(e));
    /** Notes whether this press is dismissing a colour picker. */
    const onDown = () => {
      pickerWasOpen = isColorPickerOpen();
    };
    /**
     * Drops a pin at the click.
     *
     * @param {MapMouseEvent} e - The click.
     */
    const onClick = (e: MapMouseEvent) => {
      if (pickerWasOpen) return;
      dropPin(eventLngLat(e), { fly: false });
    };
    // The crosshair is set inline, which outranks the closed hand Mapbox's stylesheet
    // shows while panning, so the pan has to put the hand back itself.
    /** Shows the closed hand while the map is being dragged. */
    const onDragStart = () => {
      canvas.style.cursor = 'grabbing';
    };
    /** Back to the crosshair once the drag ends. */
    const onDragEnd = () => {
      canvas.style.cursor = 'crosshair';
    };
    map.on('mousemove', onMove);
    if (!clicksTaken) {
      map.on('mousedown', onDown);
      map.on('click', onClick);
      map.on('dragstart', onDragStart);
      map.on('dragend', onDragEnd);
      canvas.style.cursor = 'crosshair';
    }
    return () => {
      map.off('mousemove', onMove);
      map.off('mousedown', onDown);
      map.off('click', onClick);
      map.off('dragstart', onDragStart);
      map.off('dragend', onDragEnd);
      if (['crosshair', 'grabbing'].includes(canvas.style.cursor)) {
        canvas.style.cursor = '';
      }
    };
  }, [mapRef, open, clicksTaken, setCursor, dropPin]);

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) return undefined;
    /**
     * Opens the menu at the right-clicked spot.
     *
     * @param {MapMouseEvent} e - The right-click.
     */
    const onContext = (e: MapMouseEvent) => {
      // Mapbox reports right-clicks on markers too, and an edit handle's right-click
      // (delete a vertex, restore a trim) is the handle's, not a spot's.
      if (e.originalEvent.target !== map.getCanvas()) return;
      e.preventDefault();
      const { left, top } = map.getContainer().getBoundingClientRect();
      setContextMenu({
        x: left + e.point.x,
        y: top + e.point.y,
        lngLat: eventLngLat(e),
      });
    };
    /** The menu belongs to a spot on screen, so moving the map dismisses it. */
    const onMoveStart = () => setContextMenu(null);
    map.on('contextmenu', onContext);
    map.on('movestart', onMoveStart);
    return () => {
      map.off('contextmenu', onContext);
      map.off('movestart', onMoveStart);
    };
  }, [mapRef]);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  return (
    <>
      <CoordinatesControl />
      {contextMenu && (
        <MapContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          lngLat={contextMenu.lngLat}
          onDropPin={() => {
            dropPin(contextMenu.lngLat, { fly: false });
            setContextMenu(null);
          }}
          onClose={closeContextMenu}
          onCopied={() => setCopyToast('copied')}
          onCopyFailed={() => setCopyToast('failed')}
        />
      )}
      <CopyToast result={copyToast} onClose={() => setCopyToast(null)} />
    </>
  );
}
